import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { errorLogger } from "@/Common/helpers/logger";
import { ValidationError } from "joi";

// Interfaces
import { IFilterConfiguration, ISaleLogDataFilter, ISalesLogsSettings, ISystemSettings } from "@/Settings/types/Common/model";
import { IInputSalesLogsEmailDistribution, TEmailDistributionUpdateParams } from "@/Settings/types/SystemSettings/emailDistribution";
import { IMappedPosition } from "@/Common/types/positions/api";

// API's
import { updateSystemSettings } from "@/Settings/api/SystemSettings/mutations";

// Helpers
import formatEmailDistribution from "@/Settings/helpers/SystemSettings/emailDistribution/formatEmailDistribution";
import { validateSalesLogsEmailDistribution } from "@/Settings/helpers/SystemSettings/emailDistribution/validateEmailDistribution";

// MUI
import { Divider, Stack, Typography } from "@mui/material";

// Components
import Button from "@/Layouts/components/Buttons/Button";
import EmailDistributionLayout from "./EmailDistributionLayout";
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";
import InitialDataFilter from "@Settings/components/SystemSettings/GeneralContainer/sections/SalesLog/InitialDataFilter/InitialDataFilter";

// Icons
import AddCircleRounded from "@mui/icons-material/AddCircleRounded";

type TFranchise = { id: number; label: string };

type TSalesLogProps = {
	locationId: string;
	systemSettings: ISystemSettings;
	mappedPositions?: Pick<IMappedPosition, "systemPosition">[];
	franchisesList: TFranchise[];
};

export default function SalesLog(props: Readonly<TSalesLogProps>) {
	const { systemSettings, locationId, mappedPositions, franchisesList } = props;
	const { salesLogs = [] } = systemSettings;

	const [salesLogsDistribution, setSalesLogsDistribution] = useState<IInputSalesLogsEmailDistribution[]>([]);
	const [errors, setErrors] = useState<ValidationError>();
	const [selectedFranchises, setSelectedFranchises] = useState<string[]>([]);

	const { mutate: updateSalesLogsData, isPending } = useMutation({
		mutationKey: ["systemSettings", locationId],
		mutationFn: async (input: IInputSalesLogsEmailDistribution[]) => {
			const newInput = input.map((item) => {
				return {
					...item,
					toIndividuals: item.toIndividuals.map((i) => i.employeeId),
				};
			});
			return updateSystemSettings(locationId, { salesLogs: newInput }, "salesLog");
		},
		onError: (error) => errorLogger(error),
	});

	const { mutate: updateSalesLogDataFilter, isPending: isPendingSalesLogDataFilter } = useMutation({
		mutationKey: ["initialDataFilter", locationId],
		mutationFn: async (input: ISaleLogDataFilter[]) =>
			updateSystemSettings(locationId, { salesLogInitialDataFilter: input }, "SALES_LOG_INITIAL_DATA_FILTER"),
		onError: (error) => errorLogger(error),
	});

	/********************************************* HANDLERS ************************************************/
	const handleAddSalesLogsDistribution = () => {
		const updated = [...salesLogsDistribution];
		updated.push({
			toIndividuals: [],
			toPositions: [],
			exportFormat: "",
			dailyDistributions: 0,
			franchises: [...selectedFranchises],
			toEmails: [],
		});
		const { error } = validateSalesLogsEmailDistribution(updated);

		setErrors(error);
		setSalesLogsDistribution(updated);
		setSelectedFranchises([]);
	};

	const handleSalesLogsDistributionChange = ({ index, key, value }: TEmailDistributionUpdateParams) => {
		const updated = [...salesLogsDistribution];
		const updatedDistribution = { ...updated[index], [key]: value };
		updated[index] = updatedDistribution;
		setSalesLogsDistribution(updated);
		update(updated);
	};

	const handleRemove = (index: number) => {
		const updated = [...salesLogsDistribution];
		updated.splice(index, 1);
		setSalesLogsDistribution(updated);
		update(updated);
	};

	const update = (updatedSalesLogs: IInputSalesLogsEmailDistribution[]) => {
		const { isValid, error } = validateSalesLogsEmailDistribution(updatedSalesLogs);
		setErrors(error);
		if (isValid) {
			updateSalesLogsData(updatedSalesLogs);
		}
	};

	const handleUpdateInitialDataFilter = (tab: string, category: string, config: IFilterConfiguration) => {
		const updated = [...(systemSettings.salesLogInitialDataFilter || [])].map((item) => {
			if (item.name !== tab) {
				return item;
			}

			return {
				...item,
				filterOptions: item.filterOptions.map((option) => {
					if (option.name !== category) {
						return option;
					}

					return {
						...config,
					};
				}),
			};
		});

		updateSalesLogDataFilter(updated);
	};

	useEffect(() => {
		setSalesLogsDistribution(formatEmailDistribution<ISalesLogsSettings>(salesLogs || []));
		return () => {
			setSalesLogsDistribution([]);
		};
	}, [salesLogs]);

	return (
		<Stack component={"section"} mt={2} spacing={3}>
			<Typography component={"h2"} variant={"h1"}>
				Email Distribution(s)
			</Typography>
			<Stack spacing={6}>
				{salesLogsDistribution?.map((distribution, key) => {
					const keyIndex = `sales-Logs-Distribution-${key}`;
					return (
						<EmailDistributionLayout
							locationId={locationId}
							distribution={distribution}
							errors={errors}
							index={key}
							key={keyIndex}
							mappedPositions={mappedPositions}
							onRemove={handleRemove}
							label={distribution.franchises?.join(" ")}
							disabled={isPending}
							onActionUpdate={handleSalesLogsDistributionChange}
						/>
					);
				})}
			</Stack>
			<Stack spacing={2} pt={4} width={"fit-content"}>
				<SmartSelector
					multiple
					multipleSelectAll
					tagsVariant={"text"}
					selectBy={"id"}
					showBy={"label"}
					options={franchisesList}
					selected={selectedFranchises.map((item) => ({ id: item, label: item }))}
					inputProps={{
						placeholder: "Select Franchises(s)",
					}}
					listBoxProps={{
						sx: { maxHeight: "220px" },
					}}
					clearable
					sx={{ width: "200px" }}
					onChangeAction={(values) => {
						setSelectedFranchises((values as TFranchise[])?.map((item: TFranchise) => item.label));
					}}
				/>
				<Button
					variant="contained"
					text="Add Email Distribution"
					disabled={!selectedFranchises.length}
					endIcon={<AddCircleRounded sx={{ color: "white" }} />}
					onClick={handleAddSalesLogsDistribution}
				/>
			</Stack>
			<Divider />
			<InitialDataFilter
				isLoading={isPendingSalesLogDataFilter}
				systemSettings={systemSettings}
				// Callbacks
				onUpdateInitialDataFilter={handleUpdateInitialDataFilter}
			/>
		</Stack>
	);
}
